import { useState, useEffect } from "react"
import { useSelector } from "react-redux"

// types
import { RootState } from "../store/store"
import { UserProfile } from "../store/slices/user_profile.slice"

export type PersonalizationComponentType = 'attendee' | 'organizer' | 'login' | 'loading' | null

type LogicMode = 'homepage' | 'article' | 'contact-us'

// Custom hook for personalization component logic
export const usePersonalizationComponent = (
  audienceOverride?: string,
  mode: LogicMode = 'homepage'
) => {
  const hasAttemptedLogin = useSelector(
    (state: RootState) => state.metaData.hasAttemptedLogin
  )
  const userProfile = useSelector(
    (state: RootState) => state.userProfile
  ) as UserProfile | null
  const audienceEnglishCategory = useSelector(
    (state: RootState) => state.metaData.audienceEnglishCategory
  ) as RootState["metaData"]["audienceEnglishCategory"]

  const [activePersonalizationComponent, setActivePersonalizationComponent] =
    useState<PersonalizationComponentType>(null)

  const hasUserData = userProfile && (userProfile.email || userProfile.userData)
  
  // Contact-us specific logic
  const getContactUsComponent = (): PersonalizationComponentType => {
    // During loading, show generic loading component
    if (!hasAttemptedLogin) {
      return 'loading'
    }
    
    // After loading, check if user has data
    if (!hasUserData) return null
    
    const hasTickets = userProfile?.orders && userProfile.orders.length > 0
    const hasEvents = userProfile?.allData?.userEventsData?.events && 
                     userProfile.allData.userEventsData.events.length > 0
    
    // Show nothing if user has both tickets and events
    if (hasTickets && hasEvents) return null
    
    // Show MyTickets if user only has tickets
    if (hasTickets && !hasEvents) return 'attendee'

    // Show MyEvents if user only has events
    if (hasEvents && !hasTickets) return 'organizer'

    // Show nothing if user has neither
    return null
  }

  // Homepage/Article logic (existing)
  const getHomepageComponent = (): PersonalizationComponentType => {
    // Use audienceOverride if provided (for article pages), otherwise use audienceEnglishCategory (for homepage)
    const audience = audienceOverride || audienceEnglishCategory

    if (hasAttemptedLogin) {
      // After auth attempt
      if (hasUserData) {
        // User is authenticated - show based on audience
        if (audience === "Homepage variation: Attendee" || audience === "Attendee") {
          return "attendee"
        } else if (audience === "Homepage variation: Organizer" || audience === "Organizer") {
          return "organizer"
        } else {
          return null
        }
      } else {
        // User is not authenticated - show login
        return "login"
      }
    } else {
      // During auth loading - show component based on selected audience
      if (audience === "Homepage variation: Attendee" || audience === "Attendee") {
        return "attendee"
      } else if (audience === "Homepage variation: Organizer" || audience === "Organizer") {
        return "organizer"
      } else {
        return null
      }
    }
  }

  useEffect(() => {
    let component: PersonalizationComponentType = null

    switch (mode) {
      case 'contact-us':
        component = getContactUsComponent()
        break
      case 'homepage':
      case 'article':
      default:
        component = getHomepageComponent()
        break
    }

    setActivePersonalizationComponent(component)
  }, [hasAttemptedLogin, userProfile, audienceEnglishCategory, audienceOverride, mode])

  return { activePersonalizationComponent, userProfile, hasUserData, hasAttemptedLogin }
} 